import { Injectable } from '@angular/core';
import createDebug from 'debug';

const debug = createDebug('OptiMatch:PageService');

export interface PageMetadata {
  name: string;
  path: string;
  displayName: string;
  description: string;
  sidenavIcon?: string;
  isDefault: boolean;
  roles: readonly string[];
}

@Injectable({ providedIn: 'root' })
export class PageService {
  readonly names = [
    'GroupMatches',
    'GroupMatch',
    'HorseGroup',
    'HorseGroups',
    'Login',
    'Mare',
    'Mares',
    'Matches',
    'Match',
    'NewGroupMatch',
    'NewMatch',
    'NotAuthorized',
    'Stallion',
    'Stallions',
    'SwitchUser',
    'Users',
  ] as const;

  readonly metadata: readonly PageMetadata[] = [
    {
      name: 'GroupMatches',
      path: '/group-matches',
      displayName: 'Group Matches',
      description:
        "The group matches the current user has run, newest first: each horse, the group it was run against, and how many pairs it ran. The Administrator sees every user's group matches, including the runs loaded from the legacy database, and who ran each.",
      sidenavIcon: 'view_list',
      isDefault: false,
      roles: [],
    },
    {
      name: 'GroupMatch',
      path: '/group-matches/:groupMatch.id',
      displayName: 'Group Match',
      description:
        'The results of one group match under a fixed heading: when it was run and with which seed, the two influences, and the Results Summary of every pair, best 48-month average BME first. Each pair opens its own match.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'HorseGroup',
      path: '/groups/:horseGroup.id',
      displayName: 'Horse Group',
      description:
        "One group and its horses. Staff rename it or change its kind, add horses of the group's sex one at a time, and remove horses, as in the helper program. A horse can be in a group only once.",
      isDefault: false,
      roles: [],
    },
    {
      name: 'HorseGroups',
      path: '/groups',
      displayName: 'Groups',
      description:
        'The mare and stallion groups, such as books of mares and sire lines, with how many horses each holds. Search by name, and add a group.',
      sidenavIcon: 'folder_shared',
      isDefault: false,
      roles: [],
    },
    {
      name: 'Login',
      path: '/login',
      displayName: 'Login',
      description: 'Public login page with a button to initiate OIDC login.',
      isDefault: false,
      roles: ['Everyone'],
    },
    {
      name: 'Mare',
      path: '/mares/:horse.id',
      displayName: 'Mare',
      description: 'One mare: details, measurements, groups and matches.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'Mares',
      path: '/mares',
      displayName: 'Mares',
      description: 'Search, add and export mares.',
      sidenavIcon: 'female',
      isDefault: false,
      roles: [],
    },
    {
      name: 'Matches',
      path: '/matches',
      displayName: 'Matches',
      description:
        "The matches the current user has run, newest first, with each run's 48-month headline numbers. The Administrator sees every user's matches, including the runs loaded from the legacy database, and who ran each.",
      sidenavIcon: 'history',
      isDefault: true,
      roles: [],
    },
    {
      name: 'Match',
      path: '/matches/:match.id',
      displayName: 'Match',
      description:
        "The results of one match, in rows under a fixed heading: when and how it was run, the two influences, the headline numbers for 30 and 48 months, the BME chart for each age, and the measurements the run used for each horse, with today's values where they have changed. Run again with the same seed to reproduce the run, or with a new one.",
      isDefault: false,
      roles: [],
    },
    {
      name: 'NewGroupMatch',
      path: '/group-matches/new',
      displayName: 'New Group Match',
      description:
        "Runs a mare against every stallion in a group, or a stallion against every mare in a group. Choose which way round, pick the horse and the group, adjust the influences if needed (55/55 by default), and run; the results open when every pair is saved. The seed isn't shown or chosen here: a random one, or the one the query string names. Opening the page with horseId, groupId, mareSizeInfluence, sirePatternInfluence or seed in the query string fills those in.",
      isDefault: false,
      roles: [],
    },
    {
      name: 'NewMatch',
      path: '/matches/new',
      displayName: 'New Match',
      description:
        "Runs a mare against a stallion. Pick both horses, adjust the influences if needed (55/55 by default), and run; the results open when the run is saved. The seed isn't shown or chosen here: a random one of the original's 41, or the seed named in the query string, which reproduces an earlier run. Opening the page with mareId, stallionId, mareSizeInfluence, sirePatternInfluence or seed in the query string fills those in.",
      isDefault: false,
      roles: [],
    },
    {
      name: 'NotAuthorized',
      path: '/not-authorized',
      displayName: 'Not Authorized',
      description:
        'Displayed when a user authenticates but is not registered in the system.',
      isDefault: false,
      roles: ['Everyone'],
    },
    {
      name: 'Stallion',
      path: '/stallions/:horse.id',
      displayName: 'Stallion',
      description: 'One stallion: details, measurements, groups and matches.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'Stallions',
      path: '/stallions',
      displayName: 'Stallions',
      description: 'Search, add and export stallions.',
      sidenavIcon: 'male',
      isDefault: false,
      roles: [],
    },
    {
      name: 'SwitchUser',
      path: '/switch-user',
      displayName: 'Switch User',
      description:
        'Allows administrators to switch to another user by selecting from a list. Only available when ALLOW_IMPERSONATION is enabled on the server. Selecting a user sets sessionStorage and reloads the app.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'Users',
      path: '/users',
      displayName: 'Users',
      description:
        "Who can sign in, and who is an Administrator. Only people listed here can sign in, so add staff by email before they first log in. Every user can run matches; an Administrator can also manage users. An Administrator can't remove their own Administrator role.",
      sidenavIcon: 'manage_accounts',
      isDefault: false,
      roles: ['Administrator'],
    },
  ];

  readonly sidenavPages: readonly PageMetadata[] = [
    this.metadata.find((p) => p.name === 'Matches')!,
    this.metadata.find((p) => p.name === 'GroupMatches')!,
    this.metadata.find((p) => p.name === 'Mares')!,
    this.metadata.find((p) => p.name === 'Stallions')!,
    this.metadata.find((p) => p.name === 'HorseGroups')!,
    this.metadata.find((p) => p.name === 'Users')!,
  ];

  getMetadata(name: string): PageMetadata | undefined {
    return this.metadata.find((m) => m.name === name);
  }
}
